import {isFilled} from '@prismicio/client'
import {SliceZone} from '@prismicio/react'
import type {ButtonDocument, EventDocument, NavigationDocumentData} from 'prismicio-types'
import {memo} from 'react'
import {ToastProvider} from 'react-toast-notifications'
import {components} from 'slices'
import {Container} from 'src/common/Container'
import {Layout} from 'src/common/Layout'
import {Meta} from 'src/common/Meta'
import {PageFaqSEO} from 'src/common/PageFaqSEO'
import type {ParentUid} from 'src/common/utils/documentHelpers'
import {getAlternateLanguagesWithParent, getMetaProps} from 'src/common/utils/documentHelpers'
import {AboutSection} from './components/AboutSection'
import {Hero} from './components/Hero'
import {RegisterForm} from './components/RegisterForm'
import {RelatedEventsCarousel} from './components/RelatedEventsCarousel'

export const Event = memo(function (props: {
  navigationData: NavigationDocumentData
  page: EventDocument
  parentUids?: Array<ParentUid>
  buttonsData?: Array<ButtonDocument>
  relatedEvents?: Array<EventDocument>
}) {
  const data = props.page.data
  const onDemand = data.event_type === 'On demand'

  const relatedEvents = props.relatedEvents ?? []
  const shouldRenderRelatedEvents = relatedEvents.length > 0

  const isSliceZoneFilled = isFilled.sliceZone(data.slices)

  return (
    <ToastProvider autoDismiss={5000}>
      <Layout
        alternateLanguages={getAlternateLanguagesWithParent(props.page.alternate_languages, props.parentUids)}
        navigationData={props.navigationData}
        pageUid={props.page.uid}
        locale={props.page.lang}
        parentUids={props.parentUids}
      >
        <Meta {...getMetaProps(props.page)} />
        <PageFaqSEO slices={data.slices} />
        <Hero data={data} pageUid={props.page.uid} />
        <AboutSection
          aboutTitle={data.about_section_title}
          aboutDescription={data.about_description}
          speakersTitle={data.speakers_section_title}
          speakers={data.speakers}
        />
        {(!!data.form_fields.length || !!data.consent_text.length) && !onDemand && (
          <RegisterForm data={data} pageUid={props.page.uid} />
        )}
        {isSliceZoneFilled && <div className="pt-0 lg:pt-8" />}
        <SliceZone
          components={components}
          context={{
            pageUid: props.page.uid,
            buttonsData: props.buttonsData,
          }}
          slices={data.slices}
        />
        {shouldRenderRelatedEvents && (
          <Container
            backgroundColor="Grey/100"
            className={`flex-col gap-6 !pb-0 ${isSliceZoneFilled ? '!pt-0 lg:!pt-8' : '!pt-20 sm:!pt-8 lg:!pt-20'}`}
          >
            <div className="mx-auto flex w-full max-w-[1200px] px-5 md:px-10 lg:px-5 xl:px-0">
              {isFilled.keyText(data.related_events_title) && (
                <h2 className="text-24 font-medium leading-normal max-md:tracking-[-.24px] md:leading-10">
                  {data.related_events_title}
                </h2>
              )}
            </div>
            <RelatedEventsCarousel
              events={relatedEvents}
              labels={{
                watchButton: data.watchButton || 'WATCH',
                registerButton: data.registerButton || 'REGISTER',
              }}
            />
          </Container>
        )}
        <div className="pb-10 md:pb-20" />
      </Layout>
    </ToastProvider>
  )
})
